import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { merge } from 'lodash';

import { CarrierContractsApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { tryParseJson } from '~src/utils/json';

import { CarrierContractDetail } from '../queries';

function sortSchemaByUiSchema(
  schema: Record<string, Schema>,
  uiSchema: Record<string, UISchema>
): Record<string, Schema> {
  if (!schema || !uiSchema) {
    return schema;
  }

  // 1. Get and sort the keys based on 'sort_order'
  const sortedKeys = Object.keys(schema).sort((keyA, keyB) => {
    // Default sort_order is 0 for fields without one defined
    const sortA = uiSchema[keyA]?.sort_order ?? 0;
    const sortB = uiSchema[keyB]?.sort_order ?? 0;

    // Primary sort: by defined sort_order
    if (sortA !== sortB) {
      return sortA - sortB;
    }

    // Secondary sort: alphabetical by key name (stable sort)
    return keyA.localeCompare(keyB);
  });

  // 2. Build the new sorted schema object and recurse on nested objects
  const sortedSchema: Record<string, Schema> = {};

  for (const key of sortedKeys) {
    const propertySchema = schema[key];
    const propertyUiSchema = uiSchema[key];

    if (
      propertySchema?.type === 'object' &&
      propertySchema.properties &&
      propertyUiSchema &&
      typeof propertyUiSchema === 'object'
    ) {
      // Recursive call for nested 'object' types
      sortedSchema[key] = {
        ...propertySchema,
        properties: sortSchemaByUiSchema(propertySchema.properties, propertyUiSchema as Record<string, UISchema>),
      };
    } else {
      // Copy primitive types or non-sortable objects/arrays
      sortedSchema[key] = propertySchema;
    }
  }

  // Type assertion to maintain the generic return type T
  return sortedSchema;
}

export interface OneOf {
  const: number;
  title: string;
}

export type Schema = { title?: string; description?: string } & (
  | { type: 'string'; value?: string }
  | { type: 'number'; value?: number }
  | { type: 'boolean'; value?: boolean }
  | { type: 'object'; properties: Record<string, Schema> }
  | { type: 'array'; items: Schema; value?: string[] }
  | { type: 'integer'; oneOf?: OneOf[]; value?: number }
);

export interface UISchema {
  default_value?: string | number | boolean;
  sort_order?: number;
  readonly?: boolean;
  widget?: string;
  description?: string;
  optional?: boolean;
}

export type SchemaValues = Record<
  string,
  {
    value?: string | number | string[];
    properties?: SchemaValues;
  }
>;

export function useCarrierContractSchemaQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['carrierContractSchemas', siteId],
    lists: () => [...keys.all, 'list'] as const,
    details: () => [...keys.all, 'detail'] as const,
    detail: (id: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useCarrierContractAuthFormSchemaQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['carrierContractAuthFormSchemas', siteId],
    details: () => [...keys.all, 'detail'] as const,
    detail: (id: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useCarrierContractsListSchemasQuery() {
  const queryKey = useCarrierContractSchemaQueryKey();

  const { data, ...query } = useSuspenseQuery({
    queryFn: async () => {
      const { data } = await CarrierContractsApiInstance.schemas.carrierContractsListSchemas({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        ingrid_products: 2 as any, // future (create contracts for different products)
      });

      return data;
    },
    queryKey: queryKey.lists(),
  });

  return {
    data: data.contract_schemas ?? [],
    ...query,
  };
}

export function useCarrierContractSchemaQuery(schemaId: string) {
  const queryKey = useCarrierContractSchemaQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await CarrierContractsApiInstance.schemas.carrierContractsGetContractSchema(schemaId);

      const jsonSchema = JSON.parse(data.json_schema!) as Record<string, Schema>;
      const uiSchema = JSON.parse(data.ui_schema!) as Record<string, UISchema>;

      const authJsonSchema = tryParseJson(data.authentication_form_json_schema) as
        | Record<string, { type: 'object'; properties: Record<string, Schema>; title?: string; description?: string }>
        | undefined;

      const authUiSchema = tryParseJson(data.authentication_form_ui_schema) as Record<string, UISchema> | undefined;

      // We need to handle the case where the main json_schema is an object,
      // and its properties should be sorted based on the root of the ui_schema.
      const sortedJsonSchema = sortSchemaByUiSchema(jsonSchema, uiSchema);
      let sortedAuthJsonSchema = authJsonSchema;

      if (authJsonSchema && authUiSchema) {
        const sortedSteps: typeof authJsonSchema = {};

        for (const stepKey in authJsonSchema) {
          const stepSchema = authJsonSchema[stepKey];
          const stepUiSchema = authUiSchema[stepKey];

          // Check if the step schema has properties to sort
          if (stepSchema.properties && stepUiSchema) {
            sortedSteps[stepKey] = {
              ...stepSchema,
              // Sort the properties within the step
              properties: sortSchemaByUiSchema(stepSchema.properties, stepUiSchema as Record<string, UISchema>),
            };
          } else {
            sortedSteps[stepKey] = stepSchema;
          }
        }
        sortedAuthJsonSchema = sortedSteps;
      }

      return {
        authentication_form_json_schema: sortedAuthJsonSchema,
        authentication_form_ui_schema: authUiSchema,
        json_schema: sortedJsonSchema,
        ui_schema: uiSchema,
      };
    },
    queryKey: queryKey.detail(schemaId),
    enabled: !!schemaId,
  });
}

export function useCarrierContractCombinedSchemaQuery(
  contract?: Pick<CarrierContractDetail, 'carrier_configuration_data' | 'schema_id'>
) {
  const { data, ...query } = useCarrierContractSchemaQuery(contract?.schema_id ?? '');

  return {
    data: {
      json_schema: merge({}, data?.json_schema ?? {}, valueify(contract?.carrier_configuration_data ?? {})),
      ui_schema: data?.ui_schema ?? {},
    },
    ...query,
  };
}

/**
 * In order to merge values into the combined schema we first have
 * to transform the values into a similar structure, which is done via this function.
 *
 * Input:
 * { "api_key": "abc-123" }
 *
 * Output:
 * { "api_key": { "value": "abc-123" } }
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function valueify(obj: Record<string, any>): SchemaValues {
  const transformed: SchemaValues = {};

  for (const key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        transformed[key] = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          properties: valueify(obj[key]),
        };
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        transformed[key] = { value: obj[key] };
      }
    }
  }

  return transformed;
}
